import type { CSSProperties, ReactNode } from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ************** Types and Interfaces **************
export type HIProgressBarProps = {
    value: number;
    min?: number;
    max?: number;
    label?: ReactNode;
    showPercentage?: boolean;
    labelFormatter?: (percentage: number, value: number) => ReactNode;
    labelPosition?: 'top' | 'right' | 'left' | 'inside';
    barColor?: string;
    trackColor?: string;
    height?: number;
    borderRadius?: number;
    labelColor?: string;
    ariaLabel?: string;
    className?: string;
    style?: CSSProperties;
};


/**
 * Clamps a numeric value to the specified range [min, max].
 * Ensures the value does not go below min or above max, useful for progress bar calculations.
 * @param value - The value to clamp.
 * @param min - The minimum value of the range (inclusive).
 * @param max - The maximum value of the range (inclusive). Should be >= min.
 * @returns The clamped value within [min, max].
 */
function clamp(value: number, min: number, max: number): number {
    if (value < min) {
        return min;
    }
    if (value > max) {
        return max;
    }
    return value;
}

/**
 * Applies the label template by replacing placeholders with actual values.
 * Supported placeholders: {percentage}, {value}, {min}, {max}.
 * @param label - The label template string containing placeholders.
 * @param percentage - The calculated percentage value to replace {percentage}.
 * @param value - The current value to replace {value}.
 * @param min - The minimum value to replace {min}.
 * @param max - The maximum value to replace {max}.
 * @returns The formatted label string with placeholders replaced by actual values.
 */
function applyLabelTemplate(
    label: string,
    percentage: number,
    value: number,
    min: number,
    max: number,
): string {
    return label
        .replace(/{percentage}/g, String(Math.round(percentage)))
        .replace(/{value}/g, String(value))
        .replace(/{min}/g, String(min))
        .replace(/{max}/g, String(max));
}

/**
 * ProgressBar component that displays a linear progress bar with optional label and customization.
 * @param props - The properties for configuring the ProgressBar.
 * @returns A React element representing the ProgressBar.
 */
export default function HIProgressBar({
    value,
    min = 0,
    max = 100,
    label,
    showPercentage = true,
    labelFormatter,
    labelPosition = 'right',
    barColor,
    trackColor,
    height = 8,
    borderRadius = 4,
    labelColor,
    ariaLabel = 'progress',
    className,
    style,
}: HIProgressBarProps) {
    const theme = useTheme();
    const resolvedBarColor = barColor ?? theme.palette.primary.main;
    const resolvedTrackColor = trackColor ?? theme.palette.action.hover;
    const resolvedLabelColor = labelColor ?? theme.palette.primary.contrastText;

    const safeMax = max <= min ? min + 1 : max;
    const clampedValue = clamp(value, min, safeMax);
    const percentage = ((clampedValue - min) / (safeMax - min)) * 100;

    let computedLabel: ReactNode;
    if (typeof label === 'string') {
        computedLabel = applyLabelTemplate(label, percentage, clampedValue, min, safeMax);
    } else if (label != null) {
        computedLabel = label;
    } else if (labelFormatter) {
        computedLabel = labelFormatter(percentage, clampedValue);
    } else if (showPercentage) {
        computedLabel = `${Math.round(percentage)}%`;
    }

    const linearProgress = (
        <LinearProgress
            variant="determinate"
            value={percentage}
            aria-label={ariaLabel}
            sx={{
                height,
                borderRadius,
                bgcolor: resolvedTrackColor,
                '& .MuiLinearProgress-bar': {
                    borderRadius,
                    backgroundColor: resolvedBarColor,
                },
            }}
        />
    );

    if (!computedLabel) {
        return (
            <Box className={className} style={style}>
                {linearProgress}
            </Box>
        );
    }

    if (labelPosition === 'inside') {
        return (
            <Box className={className} style={style}>
                <Box sx={{ position: 'relative' }}>
                    {linearProgress}
                    <Typography
                        variant="caption"
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: resolvedLabelColor,
                            fontWeight: 600,
                            lineHeight: 1,
                            pointerEvents: 'none',
                        }}
                    >
                        {computedLabel}
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (labelPosition === 'top') {
        return (
            <Box className={className} style={style}>
                <Typography
                    variant="body2"
                    sx={{ mb: 0.5, display: 'flex', justifyContent: 'center', color: resolvedLabelColor }}
                >
                    {computedLabel}
                </Typography>
                {linearProgress}
            </Box>
        );
    }

    if (labelPosition === 'left') {
        return (
            <Box className={className} style={style}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Typography variant="body2" sx={{ minWidth: 44, color: resolvedLabelColor }}>
                        {computedLabel}
                    </Typography>
                    <Box sx={{ flex: 1 }}>{linearProgress}</Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box className={className} style={style}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box sx={{ flex: 1 }}>{linearProgress}</Box>
                <Typography variant="body2" sx={{ minWidth: 44, textAlign: 'right', color: resolvedLabelColor }}>
                    {computedLabel}
                </Typography>
            </Box>
        </Box>
    );
}
